import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';
import { extractSnapshotOptions } from '../../../lib/snapshot-search';

/**
 * Dominant Base Trend
 * 
 * Simply passes through the trendSignal/signalTheme calculated by calculateTrendAnalysis
 * which is exactly what the StrikeTrendWidget UI uses.
 */
export const evaluateDominantBaseTrend: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data || !context.liveIndicators) return null;

    const { trendAnalysisData } = context.liveIndicators;
    if (!trendAnalysisData) return null;

    const { signalTheme, trendSignal } = trendAnalysisData;

    let status = 'Sideways';
    if (signalTheme === 'bull') status = 'BULL';
    else if (signalTheme === 'bear') status = 'BEAR';

    if (status === 'Sideways') return null;

    const spot = data.spotPrice || data.index_ltp || data.ltp || 0;
    if (spot <= 0) return null;

    const atmStrike = Math.round(spot / context.gap) * context.gap;
    
    // Ensure we have flat options so we can easily pick out CE or PE premiums
    const liveRawOptsArr = context.chain || extractSnapshotOptions(data, context.symbol, context.expiry) || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);

    const optionType = status === 'BULL' ? 'CE' : 'PE';
    let entryPrice = 0;
    
    const atmRow = liveFlatOpts.find(r => r.strike === atmStrike && r.type === optionType);
    if (atmRow) {
        entryPrice = atmRow.ltp || atmRow.close || atmRow.price || 0;
    }
    
    if (entryPrice <= 0) return null;

    return {
        trade_type: status as any,
        strike: atmStrike,
        option_type: optionType,
        entry_price: entryPrice,
        remarks: `Dominant Trend (${trendSignal})`
    };
};
